import { apiErrorMessage } from '@agos/api-client'
import {
  useGetSuperAdminFranchisesFranchiseIdPartners,
  getGetSuperAdminFranchisesQueryKey,
  useDeleteSuperAdminFranchisesFranchiseId,
  useDeleteSuperAdminFranchisesFranchiseIdAdminsAdminId,
  useGetSuperAdminAdmins,
  useGetSuperAdminFranchises,
  useGetSuperAdminFranchisesFranchiseIdEffectiveModules,
  useGetSuperAdminModules,
  useGetSuperAdminPackages,
  usePatchSuperAdminFranchisesFranchiseId,
  usePatchSuperAdminFranchisesFranchiseIdAdminsAdminId,
  usePostSuperAdminFranchisesFranchiseIdSsoToken,
  usePutSuperAdminFranchisesFranchiseIdOverrides,
  useGetSuperAdminFranchisesFranchiseIdPackage,
  usePutSuperAdminFranchisesFranchiseIdPackage,
  useDeleteSuperAdminFranchisesFranchiseIdWireguard,
  usePostSuperAdminFranchisesFranchiseIdWireguardProvision,
  getSuperAdminFranchisesFranchiseIdWireguardConfig,
} from '@agos/api-client/platform'
import {
  T,
  downloadTextFile,
  expectedIcDph,
  formatDecimal,
  icDphMatchesDic,
  tt,
  useModalFocus,
} from '@agos/shared'
import { PageHeader, cn, ConfirmDialog, toast, ReadFailed, Spinner, StatusBadge } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, Download, Eye, EyeOff, LogIn, Network, Save, Trash2 } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'wouter'

import { CustomerDomainSection } from './CustomerDomainSection'
import { FranchiseBillingItemsSections } from './FranchiseBillingItemsSections'
/* §3.184 - the partner rules, which moved into this module so this page could read them. */
import {
  allocationNote,
  isOverAllocated,
  sharesAreUnknown,
  sharesNote,
  sharesOf,
  sharesState,
} from './partners'


import {
  adminActionNote,
  adminActivePatch,
  adminToggle,
  adminsOf,
  type AdminAction,
  draftOfFranchise,
  eraseConfirmed,
  erasePrompt,
  tunnelConfigFilename,
  tunnelProvisioned,
  tunnelRemovalConfirmed,
  tunnelRemovalPrompt,
  exceptionCount,
  franchiseDraftProblem,
  franchisePatch,
  hideRefusal,
  jumpBlockedNote,
  jumpWarning,
  mayDeactivate,
  mayHide,
  mayJump,
  mayOverride,
  overrideLockReason,
  overrideRows,
  overridesByKey,
  overridesChanged,
  overridesPayload,
  packagePayload,
  passwordProblem,
  redirectUrlOf,
  retirementNote,
  softLaunchNote,
  softLaunchPatch,
  softLaunchState,
  tierChanged,
  tierModuleKeys,
  THEME_CHOICES,
  assignmentChanged,
  assignmentOf,
  type TierAssignment,
  wouldHave,
  type FranchiseDraft,
  type ModuleOverrideRow,
  type OverrideState,
} from './franchise-detail'

/**
 * One franchise: what it is, where to go from it, what tier it is on, and how to take it out of
 * service.
 *
 * The jump is gated on what actually makes it work — an active `main_admin` and a manager domain —
 * rather than on a round trip that ends in an `alert()`. Two of the three franchises have neither
 * administrator, so the button is off for both and says why.
 *
 * **The write half is `SAAS.md` §3.5, the owner's A14** — *"there are no functionality at all.. and we
 * need it 1:1 except design."* FB-505 measured the Tower calling 3 of 44 write endpoints, and this
 * page was the clearest example: an erase confirmation that makes somebody type the slug out, with
 * **no call behind the button**. Four are wired now — edit, re-tier, deactivate, erase.
 *
 * **The confirmation's shape is kept, not modernised away.** §3.5 says that plainly: *1:1* licenses
 * wiring a destructive action, never making it easier to reach. Typing the slug stays, and the dialog
 * beside it now says which of the two things is about to happen, because `DELETE` deactivates and
 * `?force=true` removes the registry row.
 */
export function FranchiseDetailPage() {
  const params = useParams<{ id: string }>()
  const franchiseId = Number(params.id ?? 0)
  const franchises = useGetSuperAdminFranchises()
  const admins = useGetSuperAdminAdmins()
  const packages = useGetSuperAdminPackages()
  const sso = usePostSuperAdminFranchisesFranchiseIdSsoToken()
  const patch = usePatchSuperAdminFranchisesFranchiseId()
  const retire = useDeleteSuperAdminFranchisesFranchiseId()
  const setTier = usePutSuperAdminFranchisesFranchiseIdPackage()
  /**
   * §3.70 - the tunnel, at the owner's *"tunnel only, never the restore"*. Both writes had no caller
   * in the Tower; `POST …/restore-database` still has none, deliberately, and the panel says so.
   */
  const provisionTunnel = usePostSuperAdminFranchisesFranchiseIdWireguardProvision()
  const removeTunnel = useDeleteSuperAdminFranchisesFranchiseIdWireguard()
  /**
   * §3.38 - the read that had no caller, and whose absence was the bug.
   *
   * `PUT .../package` upserts the theme override, the negotiated price and the notes from the body,
   * so a payload naming only the tier wrote NULL over all three. Nothing could preserve them because
   * nothing read them. Legacy calls this endpoint and threads the stored values through both of its
   * controls, which is why legacy does not lose the theme.
   */
  const assignmentRead = useGetSuperAdminFranchisesFranchiseIdPackage(franchiseId)
  const storedAssignment = useMemo(
    () => assignmentOf(assignmentRead.data),
    [assignmentRead.data],
  )
  const [assignmentEdits, setAssignmentEdits] = useState<Partial<TierAssignment>>({})
  const assignment: TierAssignment = { ...storedAssignment, ...assignmentEdits }

  // ─── §3.7 - the negotiated exceptions, and the administrators ──────────────
  const catalogue = useGetSuperAdminModules()
  const effective = useGetSuperAdminFranchisesFranchiseIdEffectiveModules(franchiseId)
  const putOverrides = usePutSuperAdminFranchisesFranchiseIdOverrides()
  const patchAdmin = usePatchSuperAdminFranchisesFranchiseIdAdminsAdminId()
  const removeAdmin = useDeleteSuperAdminFranchisesFranchiseIdAdminsAdminId()

  const queryClient = useQueryClient()
  const [typed, setTyped] = useState('')
  /** §3.70 - the tunnel's own typed confirmation, separate from the erase one above it. */
  const [tunnelTyped, setTunnelTyped] = useState('')
  const [downloadingConfig, setDownloadingConfig] = useState(false)
  const [confirming, setConfirming] = useState<'deactivate' | 'erase' | null>(null)
  // §3.13 - the soft-launch switch's own confirmation. Separate from `confirming` because it is not
  // a retirement: hiding a branch does not take it offline, and sharing that dialog would make the
  // two read as the same kind of act.
  const [softLaunching, setSoftLaunching] = useState<boolean | null>(null)
  // **Only what was edited is state.** The draft is derived from the server's row beneath it, so
  // there is no effect seeding it - and an effect was the first shape, which lint refused and was
  // right to: the franchise arrives on a later render than the first, so seeding it in an effect is
  // a cascading render and a draft that is empty until it is not.
  const [edits, setEdits] = useState<Partial<FranchiseDraft>>({})
  const [chosenTier, setChosenTier] = useState<number | null | undefined>(undefined)
  // Same shape as `edits` above and for the same reason: only what was changed is state, and the
  // rest is derived from the server's rows beneath it.
  const [overrideEdits, setOverrideEdits] = useState<Record<string, OverrideState>>({})
  const [adminAction, setAdminAction] = useState<
    { id: number; name: string; kind: AdminAction } | null
  >(null)
  const [newPassword, setNewPassword] = useState('')

  /* §9.495, `TODO.md` 3f - the admin editor said `aria-modal="true"` and confined nothing. **Its wrapper is not
     a dismiss scrim** (it carries no `onClick`), so the only way out is the cancel button inside and the close is
     that button's own handler. Guarded on the two writes it can start (§9.480). */
  const adminPanelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown: adminKeyDown } = useModalFocus(
    adminAction !== null,
    adminPanelRef,
    patchAdmin.isPending || removeAdmin.isPending ? null : () => setAdminAction(null),
  )

  const allFranchises = useMemo(
    () =>
      Array.isArray((franchises.data as { franchises?: unknown } | undefined)?.franchises)
        ? (franchises.data as { franchises: Record<string, unknown>[] }).franchises
        : [],
    [franchises.data],
  )
  const franchise = useMemo(
    () => allFranchises.find((row) => row.id === franchiseId) ?? null,
    [allFranchises, franchiseId],
  )

  const staff = useMemo(() => adminsOf(admins.data), [admins.data])
  const slug = typeof franchise?.slug === 'string' ? franchise.slug : ''
  /**
   * §3.70 - the tunnel's three safe fields, read the way `slug` above is.
   *
   * The payload is untyped here, and `unknown` is the honest type for it: FB-624 removed
   * `wireguardConfig` and `masterApiKey` from this endpoint, so what arrives is the peer's uuid, its
   * client IP, its **public** key and a `hasWireguardConfig` flag - and a field that is absent has
   * to read as absent rather than as the string `"undefined"`.
   */
  const tunnel = useMemo(
    () => ({
      wireguardPeerUuid:
        typeof franchise?.wireguardPeerUuid === 'string' ? franchise.wireguardPeerUuid : null,
      wireguardClientIp:
        typeof franchise?.wireguardClientIp === 'string' ? franchise.wireguardClientIp : '',
      wireguardPublicKey:
        typeof franchise?.wireguardPublicKey === 'string' ? franchise.wireguardPublicKey : '',
      hasWireguardConfig: franchise?.hasWireguardConfig === true,
    }),
    [franchise],
  )
  const managerDomain = typeof franchise?.managerDomain === 'string' ? franchise.managerDomain : null
  // §3.13. **Absent reads as visible**, matching `registryOf` on the customer side: a registry not
  // yet answering the field has no hidden branches, and defaulting the other way would show every
  // branch as hidden on a stack that has not run `V4`.
  const isHidden = franchise?.hiddenFromCustomerPicker === true
  const managerGroup =
    typeof franchise?.managerGroup === 'string' ? franchise.managerGroup : null
  /**
   * How many branches in this group customers are offered.
   *
   * A franchise with no group is its own group of one, which is why the fallback is 1 rather than 0:
   * a standalone branch has nothing to be the last visible one *of*, and `mayHide` refusing it is
   * correct - hiding it would leave its own picker empty.
   */
  const visibleInGroup =
    managerGroup === null
      ? 1
      : allFranchises.filter(
          (row) =>
            (typeof row.managerGroup === 'string' ? row.managerGroup : null) === managerGroup &&
            row.isActive !== false &&
            row.hiddenFromCustomerPicker !== true,
        ).length
  const isActive = franchise?.isActive === true
  const blocked = jumpBlockedNote(staff, franchiseId, managerDomain)
  const stored = useMemo(() => draftOfFranchise(franchise), [franchise])
  /**
   * §3.38 - the tier the franchise is actually on, from the assignment row.
   *
   * **This read `franchise?.packageId` and that field does not exist.** `GET /super-admin/franchises`
   * answers `package` as `{slug, name}` - there is no `packageId` on it and no `package_id` column on
   * `franchises` - so `currentTier` was **always null**: the picker showed *Žiadny balíček* for all
   * three franchises, every one of which is on Premium. It went unnoticed because
   * `tierChanged(tier, null)` is true for any choice, so the save button behaved normally.
   *
   * `GET .../package` is what answers this, and nothing called it. One uncalled read, two defects.
   */
  const currentTier = storedAssignment.packageId
  const draft: FranchiseDraft = useMemo(() => ({ ...stored, ...edits }), [stored, edits])
  // `undefined` means *nobody has touched the picker*, which is not the same as *no package*. A
  // single nullable would make "cleared it" and "has not chosen" the same state, and the save button
  // reads that difference.
  const tier = chosenTier === undefined ? currentTier : chosenTier

  // ─── §3.7 ──────────────────────────────────────────────────────────────────
  //
  // The rows are the whole catalogue with the franchise's tier and its stored exceptions folded in,
  // and then whatever has been changed on top. `overridesByKey` is the reader; `overrideRows` does
  // the fold. Nothing here decides what an override means - `franchise-detail.ts` does, and its
  // tests hold the four causes a module can be on for.
  /**
   * The exceptions this franchise actually has stored - **FB-647, §3.61.**
   *
   * This read `effective.data.overrides`, and `GET .../effective-modules` answers
   * `{"modules": [...]}` and nothing else (`PlatformTierService:393`, two lines, never an
   * `overrides` key). So the map was **empty on every load**: the heading counted nought exceptions
   * for a franchise with two, both rows rendered as *Podľa balíčka*, and - because
   * `PUT .../overrides` **replaces** rather than merges and `overridesPayload` sends only rows whose
   * state is not `inherit` - **a save deleted every stored override**, which is the opposite of what
   * the note beside its button promises.
   *
   * `GET .../package` is the endpoint that answers this, it is already called on this page (§3.38
   * added it so the tier save would stop clobbering the negotiated fields), and its rows carry
   * `moduleKey` and `granted` - the shape `overridesByKey` already reads.
   *
   * **`effective.data` keeps its own job**: it says what the franchise has *after all three layers*,
   * which is the fact the row badges compare a pending choice against. Two questions, two endpoints.
   */
  const storedOverrides = useMemo(
    () => overridesByKey((assignmentRead.data as { overrides?: unknown } | undefined)?.overrides),
    [assignmentRead.data],
  )
  /**
   * §3.189, FB-966 - **this read could not succeed, and FB-647's own fix is the comment above it.**
   *
   * It was `Array.isArray(packages.data) ? packages.data : []` against an endpoint that answers
   * `Map.of("packages", …)`, so the list was empty on every load and `wouldHave` drew **`nemá`** for all 209
   * modules `pizzamajstro` grants - on all three of the owner's franchises, beside an effective-modules panel
   * saying the opposite. `tiers` thirty lines below reads the same payload correctly, which is what says this
   * was a slip rather than a shape nobody knew.
   *
   * In `franchise-detail.ts` because a `useMemo` on this page is reachable by no test, and because the function
   * that consumes the answer lives there with its own cases.
   */
  const tierModules = useMemo(() => tierModuleKeys(packages.data, tier), [packages.data, tier])
  const overrides: ModuleOverrideRow[] = useMemo(() => {
    const rows = overrideRows(
      Array.isArray(catalogue.data)
        ? (catalogue.data as { key?: string; name?: string; category?: string; alwaysOn?: boolean }[])
        : (((catalogue.data as { modules?: unknown } | undefined)?.modules ?? []) as {
            key?: string
            name?: string
            category?: string
            alwaysOn?: boolean
          }[]),
      tierModules,
      storedOverrides,
    )
    return rows.map((row) =>
      // Destructured once rather than indexed twice, which also drops an assertion the compiler
      // already knew was unnecessary.
      ((edited) => (edited === undefined ? row : { ...row, state: edited }))(
        overrideEdits[row.key],
      ),
    )
  }, [catalogue.data, tierModules, storedOverrides, overrideEdits])
  const exceptions = exceptionCount(overrides)
  const mine = useMemo(
    () => staff.filter((one) => one.franchiseId === franchiseId),
    [staff, franchiseId],
  )

  const saveOverrides = () =>
    putOverrides.mutate(
      { franchiseId, data: overridesPayload(overrides) },
      {
        onSuccess: () => {
          setOverrideEdits({})
          void queryClient.invalidateQueries()
          toast({ title: tt('Výnimky uložené') })
        },
        // §9.385 - `description: ''` on a Tower write. The exemption list is validated server-side.
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Výnimky sa nepodarilo uložiť'),
            description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
          }),
      },
    )

  const tiers = useMemo(() => {
    const rows = (packages.data as { packages?: unknown } | undefined)?.packages
    return Array.isArray(rows) ? (rows as Record<string, unknown>[]) : []
  }, [packages.data])

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getGetSuperAdminFranchisesQueryKey() })

  /**
   * A refused write, said in the server's words - §9.360.
   *
   * This took `{ body?: { error?: string } }` and read `failure.body?.error`. **`ApiError` has no
   * `body`** - `custom-fetch.ts:189` puts the envelope in `data` - so the fallback was the only
   * message it could produce, on a screen whose server refusals are specific: §3.56's identity
   * fields are validated there, and *"IČ DPH nemá správny formát"* read as *"Uloženie zlyhalo"*.
   */
  const failed = (failure: unknown, fallback: string) =>
    toast({ title: apiErrorMessage(failure, fallback), variant: 'destructive' })

  const save = () => {
    const problem = franchiseDraftProblem(draft)
    if (problem !== null) {
      toast({ title: problem, variant: 'destructive' })
      return
    }
    const body = franchisePatch(draft, stored)
    if (Object.keys(body).length === 0) {
      return
    }
    void patch
      .mutateAsync({ franchiseId, data: body })
      .then(async () => {
        await refresh()
        // Cleared, so the derived draft goes back to reading the server's row. Keeping them would
        // leave an untrimmed value on screen the moment the server normalised one, and the save
        // button - which compares the draft with the row - would stay enabled over nothing.
        setEdits({})
        toast({ title: tt('Franšíza uložená') })
      })
      .catch((failure: unknown) =>
        failed(failure, tt('Uloženie zlyhalo')),
      )
  }

  const applyTier = () => {
    void setTier
      .mutateAsync({ franchiseId, data: packagePayload(tier, assignment) })
      .then(async () => {
        await refresh()
        setChosenTier(undefined)
        setAssignmentEdits({})
        toast({ title: tt('Balíček zmenený') })
      })
      .catch((failure: unknown) =>
        failed(failure, tt('Zmena balíčka zlyhala')),
      )
  }

  /**
   * §3.13 - show or hide this branch in its siblings' customer picker.
   *
   * `softLaunchPatch` carries the one key. The text form's save sends a diff of its own fields, and
   * mixing the two would publish a branch as a side effect of correcting a phone number.
   */
  const runSoftLaunch = (hidden: boolean) => {
    setSoftLaunching(null)
    void patch
      .mutateAsync({ franchiseId, data: softLaunchPatch(hidden) })
      .then(async () => {
        await refresh()
        toast({ title: hidden ? tt('Skryté pred zákazníkmi') : tt('Zobrazené zákazníkom') })
      })
      .catch((failure: unknown) =>
        failed(failure, tt('Zmena zobrazenia zlyhala')),
      )
  }

  const runRetirement = (force: boolean) => {
    setConfirming(null)
    void retire
      .mutateAsync({ franchiseId, params: { force } })
      .then(async () => {
        await refresh()
        setTyped('')
        toast({ title: force ? tt('Registrácia odstránená') : tt('Franšíza deaktivovaná') })
      })
      .catch((failure: unknown) =>
        failed(failure, force ? tt('Odstránenie zlyhalo') : tt('Deaktivácia zlyhala')),
      )
  }

  if (franchises.isLoading) {
    return (
      <main className="flex justify-center py-16" data-franchise-detail>
        <Spinner />
      </main>
    )
  }

  // §3.107 - **before the not-found branch, because that branch is a claim.** §3.82 named this
  // exact shape as one of the worst two it found, about Marketing: *"`page === null` is reached
  // while the request is in flight and when it is refused, so an operator whose page exists was
  // told it does not - which sends somebody to create a page that is already there."* Here the
  // loading half is gated above and the failure half was not, so a refused read said *Franšíza sa
  // nenašla* about a live franchise - and acting on that means creating a second one, whose slug
  // collides with the first (§3.16).
  //
  // Found by this entry's own ratchet rather than by the census that found the other eight: the
  // census pattern counted `.error` as a hit and this file mentions it only in a comment.
  if (franchises.isError) {
    return (
      <main className="p-4 space-y-3" data-franchise-detail>
        <ReadFailed marker="franchise-detail-error"><T>
            Franšízu sa nepodarilo načítať. Neznamená to, že neexistuje - server neodpovedal.
          </T></ReadFailed>
      </main>
    )
  }

  if (franchise === null) {
    return (
      <main className="p-4" data-franchise-detail data-franchise-missing>
        <p className="text-xs text-muted-foreground">
          <T>Franšíza sa nenašla.</T>
        </p>
      </main>
    )
  }

  const field =
    'w-full min-h-[38px] px-3 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

  return (
    <main className="space-y-4" data-franchise-detail={slug}>
      {/* §3.5 - the manager puts a `PageHeader` on 31 of its 52 screens and the Tower had one on
          none. Nothing new is designed here; this is the component the rest of the product uses. */}
      <PageHeader
        title={typeof franchise.name === 'string' ? franchise.name : slug}
        description={typeof franchise.databaseUrl === 'string' ? franchise.databaseUrl : ''}
        icon={<Building2 className="w-5 h-5" />}
        actions={
          <StatusBadge
            label={isActive ? tt('Aktívna') : tt('Neaktívna')}
            color={isActive ? 'green' : 'zinc'}
          />
        }
      />
      <p className="sr-only" data-detail-db>
        {typeof franchise.databaseUrl === 'string' ? franchise.databaseUrl : ''}
      </p>

      {/* The editable fields - `PATCH /super-admin/franchises/{id}`, which had no caller. */}
      <section
        className="bg-card border border-border rounded-2xl overflow-hidden"
        data-franchise-edit
      >
        <div className="px-5 py-3 border-b border-border bg-muted/20">
          <h2 className="font-bold text-foreground text-sm">
            <T>Údaje franšízy</T>
          </h2>
        </div>
        <div className="p-5 grid gap-3 sm:grid-cols-2">
          {(
            [
              ['name', tt('Názov')],
              ['slug', tt('Slug')],
              ['contactEmail', tt('Kontaktný e-mail')],
              ['contactPhone', tt('Telefón')],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="space-y-1">
              <span className="text-[11px] font-semibold text-muted-foreground">{label}</span>
              <input
                aria-label={label}
                data-franchise-field={key}
                value={draft[key]}
                onChange={(change) =>
                  setEdits((current) => ({ ...current, [key]: change.target.value }))
                }
                className={field}
              />
            </label>
          ))}
          <label className="space-y-1 sm:col-span-2">
            <span className="text-[11px] font-semibold text-muted-foreground">
              <T>Poznámka</T>
            </span>
            <textarea
              aria-label={tt('Poznámka')}
              data-franchise-field="notes"
              rows={2}
              value={draft.notes}
              onChange={(change) => setEdits((current) => ({ ...current, notes: change.target.value }))}
              className={cn(field, 'resize-y py-2')}
            />
          </label>
        </div>

        {/* **Značka - §3.87.** Five columns `PATCH /franchises/{id}` has always accepted and no
            screen in the product could set. They are not decoration: `CentralRegistryService` serves
            `display_name`, `logo_url` and `primary_color` as the central branding payload, and the
            customer app's `branding.ts` writes `primaryColor` on top of the theme as `--primary` so
            the brand colour always wins. A franchise onboarded through AGOS therefore got the
            default theme and no logo, with nowhere to change it.

            In this section rather than its own, for the same reason the billing block is: one
            `PATCH`, one save button. */}
        <div className="px-5 pb-5 space-y-3">
          <div className="pt-4 border-t border-border">
            <h3 className="font-bold text-foreground text-xs">
              <T>Značka</T>
            </h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              <T>Čo zákazník vidí na stránke tejto franšízy. Prázdne = predvolená téma.</T>
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {(
              [
                ['displayName', tt('Zobrazovaný názov'), 'sm:col-span-2'],
                ['logoUrl', tt('URL loga'), 'sm:col-span-2'],
                ['faviconUrl', tt('URL favicony'), 'sm:col-span-2'],
                ['primaryColor', tt('Hlavná farba'), ''],
                ['secondaryColor', tt('Doplnková farba'), ''],
              ] as const
            ).map(([key, label, span]) => (
              <label key={key} className={cn('space-y-1', span)}>
                <span className="text-[11px] font-semibold text-muted-foreground">{label}</span>
                <div className="flex items-center gap-2">
                  <input
                    aria-label={label}
                    data-franchise-field={key}
                    value={draft[key]}
                    onChange={(change) =>
                      setEdits((current) => ({ ...current, [key]: change.target.value }))
                    }
                    className={field}
                  />
                  {/* A swatch, because a hex code is not a colour until somebody sees it - and a
                      typo in one is otherwise only visible on the customer's own site. */}
                  {(key === 'primaryColor' || key === 'secondaryColor') && draft[key].trim() !== '' && (
                    <span
                      data-colour-swatch={key}
                      style={{ backgroundColor: draft[key].trim() }}
                      className="w-8 h-8 rounded-lg border border-border flex-shrink-0"
                    />
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* **Manažér a skupina - §3.87.** Two more columns the server accepts and nothing could set,
            and this screen was already complaining about one of them: `jumpBlockedNote` says *"the
            franchise has no manager domain set, so there is nowhere to jump to"*, which named an
            unset field with no field to set it in. `managerGroup` decides which branches a customer
            is offered in the picker and gates the soft-launch switch through `visibleInGroup`. */}
        <div className="px-5 pb-5 space-y-3">
          <div className="pt-4 border-t border-border">
            <h3 className="font-bold text-foreground text-xs">
              <T>Manažér a skupina</T>
            </h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              <T>Doména manažéra sa používa na preskočenie do tejto franšízy. Skupina rozhoduje, ktoré pobočky sa zákazníkovi ponúknu spolu.</T>
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {(
              [
                ['managerDomain', tt('Doména manažéra'), 'sm:col-span-2'],
                ['managerGroup', tt('Skupina'), 'sm:col-span-2'],
              ] as const
            ).map(([key, label, span]) => (
              <label key={key} className={cn('space-y-1', span)}>
                <span className="text-[11px] font-semibold text-muted-foreground">{label}</span>
                <input
                  aria-label={label}
                  data-franchise-field={key}
                  value={draft[key]}
                  onChange={(change) =>
                    setEdits((current) => ({ ...current, [key]: change.target.value }))
                  }
                  className={field}
                />
              </label>
            ))}
          </div>
        </div>

        {/* **Fakturačné údaje - §3.53 queue item 2, §3.56.** Nine columns the service has accepted
            and *validated* since before this screen existed, and rendered nowhere: an operator could
            only set a franchise's invoicing identity by SQL, which is FB-505's own finding. They sit
            inside this section rather than in one of their own so the single save below still governs
            the whole row - it is one `PATCH`, and a second button would imply a second request. */}
        <div className="px-5 pb-5 space-y-3">
          <div className="pt-4 border-t border-border">
            <h3 className="font-bold text-foreground text-xs">
              <T>Fakturačné údaje</T>
            </h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              <T>Identita, na ktorú sa tejto franšíze fakturuje. Nechajte prázdne, ak zatiaľ nie je.</T>
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {(
              [
                ['billingCompanyName', tt('Obchodné meno'), 'sm:col-span-2'],
                ['billingAddress', tt('Adresa'), 'sm:col-span-2'],
                ['billingCity', tt('Mesto'), ''],
                ['billingZip', tt('PSČ'), ''],
                ['billingCountry', tt('Štát'), ''],
                ['billingEmail', tt('Fakturačný e-mail'), ''],
                ['billingIco', tt('IČO'), ''],
                ['billingDic', tt('DIČ'), ''],
                ['billingIcDph', tt('IČ DPH'), ''],
              ] as const
            ).map(([key, label, span]) => (
              <label key={key} className={cn('space-y-1', span)}>
                <span className="text-[11px] font-semibold text-muted-foreground">{label}</span>
                <input
                  aria-label={label}
                  data-franchise-field={key}
                  value={draft[key]}
                  onChange={(change) =>
                    setEdits((current) => ({ ...current, [key]: change.target.value }))
                  }
                  className={field}
                />
              </label>
            ))}
          </div>
          {/* The server refuses a mismatched pair (`BusinessIdentityCheck.requireThePairAgrees`), so
              saying so here turns a 400 on an apparently-successful save into a sentence read before
              the request. `expectedIcDph` is the same derivation the check uses. */}
          {draft.billingDic.trim() !== '' &&
            draft.billingIcDph.trim() !== '' &&
            !icDphMatchesDic(draft.billingIcDph, draft.billingDic) && (
              <p className="text-[11px] text-destructive" data-billing-pair-warning>
                <T>IČ DPH nesúhlasí s DIČ.</T>{' '}
                {tt('Podľa DIČ by malo byť')} {expectedIcDph(draft.billingDic)}.
              </p>
            )}
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center gap-2">
          <button
            type="button"
            data-franchise-save
            disabled={patch.isPending || Object.keys(franchisePatch(draft, stored)).length === 0}
            onClick={save}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/40 bg-primary/10 text-primary text-xs font-semibold disabled:opacity-40"
          >
            <Save className="w-3.5 h-3.5" /> <T>Uložiť</T>
          </button>
          {patch.isPending && <Spinner />}
          {/* Only what changed is sent: `update` writes the keys the body names, and rewriting a
              slug a tenant's whole routing is keyed on would be a side effect nobody asked for. */}
          <span className="text-[11px] text-muted-foreground" data-franchise-dirty>
            {String(Object.keys(franchisePatch(draft, stored)).length)}
          </span>
        </div>
      </section>

      {/* The tier - `PUT /super-admin/franchises/{id}/package`, which had no caller. §3 calls the
          catalogue *"drives tenant module-gating everywhere else"*, so this is the one control that
          changes what every other screen of that tenant's manager is allowed to do. */}
      <section
        className="bg-card border border-border rounded-2xl overflow-hidden"
        data-franchise-tier
      >
        <div className="px-5 py-3 border-b border-border bg-muted/20">
          <h2 className="font-bold text-foreground text-sm">
            <T>Balíček</T>
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            <T>Rozhoduje, ktoré moduly má táto franšíza k dispozícii.</T>
          </p>
        </div>
        <div className="p-5 flex flex-wrap items-center gap-2">
          {/* FB-826, §3.155 - **disabled with a note, not hidden.** `assignmentOf(undefined)` leaves `tier` null,
              so a failed read selected *Žiadny balíček* - the defect line 233 records happening before, when
              `currentTier` was always null. Hiding the control would read as *this franchise cannot have a tier*; a
              disabled one beside a sentence is the only shape that says *we do not know which it has*. And the
              action a wrong empty invites is assigning a tier, which §3.147 measured as deleting every
              `franchise_module_overrides` row. */}
          <select
            aria-label={tt('Balíček')}
            data-tier-select
            disabled={assignmentRead.isError}
            value={tier ?? ''}
            onChange={(change) =>
              setChosenTier(change.target.value === '' ? null : Number(change.target.value))
            }
            className={cn(field, 'w-auto min-w-[14rem]')}
          >
            <option value="">{tt('Žiadny balíček')}</option>
            {tiers.map((row) => (
              <option key={String(row.id)} value={String(row.id)}>
                {typeof row.name === 'string' ? row.name : String(row.slug ?? row.id)}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-tier-save
            disabled={
              (!tierChanged(tier, currentTier) &&
                !assignmentChanged(assignment, storedAssignment)) ||
              setTier.isPending ||
              /* FB-826, §3.155 - a write must not be offered from a read that failed: `assignment` is empty, so
                 saving would look like *clear the overrides* and §3.147 measured `resetOverrides` deleting every
                 `franchise_module_overrides` row. §9.540's rule - a confirmation that cannot describe its own write
                 must not offer it - applied to a save with no confirmation at all. */
              assignmentRead.isError
            }
            onClick={applyTier}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/40 bg-primary/10 text-primary text-xs font-semibold disabled:opacity-40"
          >
            <Save className="w-3.5 h-3.5" /> <T>Zmeniť balíček</T>
          </button>
          {setTier.isPending && <Spinner />}
          {/* The sentence the disabled select needs, or an operator reads a greyed control as a franchise that may
              not have a tier. Beside the control rather than in place of it, because the tier rows above are a
              different read and loaded fine. */}
          {assignmentRead.isError && (
            <p className="text-[11px] text-red-500" data-tier-unreadable>
              <T>
                Priradený balíček sa nepodarilo načítať, takže ho teraz nemeňte - nevieme, ktorý franšíza má.
              </T>
            </p>
          )}
        </div>

        {/* §3.38 - what was negotiated on top, which this endpoint was clearing on every save.
          * Three fields in one block because they are one row and one PUT: the button above sends
          * all four, so a tier change no longer discards them even if nobody touches these. */}
        <div className="px-5 pb-5 pt-0 grid grid-cols-1 md:grid-cols-3 gap-3 border-t border-border">
          <label className="block space-y-1 pt-4">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <T>Téma (override)</T>
            </span>
            <select
              data-theme-override
              value={assignment.themeOverride}
              onChange={(change) =>
                setAssignmentEdits((edits) => ({ ...edits, themeOverride: change.target.value }))
              }
              className={field}
            >
              {/* The empty choice writes `null`, which is how *use the package's theme* is spelled -
                * `themeNameFor` falls back to the package when the column is null. */}
              <option value="">{tt('Predvolená z balíčka')}</option>
              {THEME_CHOICES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 pt-4">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <T>Cena balíčka (€/mes.)</T>
            </span>
            <input
              data-price-override
              inputMode="decimal"
              value={assignment.monthlyPriceOverride}
              onChange={(change) =>
                setAssignmentEdits((edits) => ({
                  ...edits,
                  monthlyPriceOverride: change.target.value,
                }))
              }
              className={field}
            />
            <span className="block text-[10px] text-muted-foreground">
              <T>Prázdne = cena z balíčka. Ráta sa do výnosov platformy.</T>
            </span>
          </label>
          <label className="block space-y-1 pt-4">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <T>Poznámka k dohode</T>
            </span>
            <input
              data-assignment-notes
              value={assignment.notes}
              onChange={(change) =>
                setAssignmentEdits((edits) => ({ ...edits, notes: change.target.value }))
              }
              className={field}
            />
          </label>
        </div>
      </section>

      <section className="bg-card border border-border rounded-2xl p-5 space-y-2" data-jump>
        <button
          type="button"
          data-jump-button
          disabled={!mayJump(staff, franchiseId, managerDomain) || sso.isPending}
          onClick={() => {
            void sso
              .mutateAsync({ franchiseId })
              .then((answer) => {
                const url = redirectUrlOf(answer)
                if (url === null) {
                  toast({ title: tt('Server nevrátil odkaz na prihlásenie.'), variant: 'destructive' })
                  return
                }
                window.open(url, '_blank', 'noopener,noreferrer')
              })
              .catch((failure: unknown) =>
                toast({
                  title: apiErrorMessage(failure, tt('Prihlásenie do manažéra zlyhalo.')),
                  variant: 'destructive',
                }),
              )
          }}
          className="min-h-[40px] px-3 rounded-xl border border-border text-xs font-semibold disabled:opacity-40"
        >
          <T>Prihlásiť sa do manažéra</T>
        </button>
        {blocked !== '' ? (
          <p className="text-[11px] text-amber-500" data-jump-blocked>
            {blocked}
          </p>
        ) : (
          <p className="text-[11px] text-muted-foreground" data-jump-warning>
            {jumpWarning()}
          </p>
        )}
      </section>

      <nav className="flex flex-wrap gap-2 text-xs" data-detail-links>
        <Link href={`/franchises/${franchiseId}/usage`} data-detail-link="usage" className="underline">
          <T>Spotreba</T>
        </Link>
        <Link href={`/franchises/${franchiseId}/integrations`} data-detail-link="integrations" className="underline">
          <T>Integrácie</T>
        </Link>
        <Link href={`/franchises/${franchiseId}/manager-settings`} data-detail-link="manager-settings" className="underline">
          <T>Nastavenia</T>
        </Link>
      </nav>

      {/* §3.7 - the negotiated exceptions. `PUT …/{id}/overrides` had no caller, so a super-admin
          could compose tiers from every module (§3.6) and could not say *this franchise, not that
          module* without moving them to a different tier. */}
      <section
        className="bg-card border border-border rounded-2xl overflow-hidden"
        data-franchise-overrides
      >
        <div className="px-5 py-3 border-b border-border bg-muted/20">
          <h2 className="font-bold text-foreground text-sm">
            <T>Výnimky z balíčka</T>
          </h2>
          <p className="text-xs text-muted-foreground mt-1" data-overrides-note>
            <T>
              Jedna funkcia predaná nad balíček, alebo jedna odobraná. Bez výnimky platí balíček.
            </T>
          </p>
          <p className="text-[11px] text-muted-foreground mt-1" data-overrides-count>
            {exceptions.granted} <T>pridaných</T> · {exceptions.revoked} <T>odobraných</T>
          </p>
        </div>

        {catalogue.isLoading || effective.isLoading ? (
          <div className="p-5 flex justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="max-h-96 overflow-y-auto divide-y divide-border">
              {overrides.map((row) => (
                <div
                  key={row.key}
                  data-override-row={row.key}
                  className="px-5 py-2.5 flex flex-wrap items-center gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{row.name}</p>
                    <p className="text-[11px] text-muted-foreground font-mono truncate">
                      {row.key}
                      {row.inPackage ? ` · ${tt('v balíčku')}` : ''}
                    </p>
                  </div>

                  {/* §3.7 - `always_on` is not offered. `effectiveModuleKeys` adds those before it
                      reads the package or the overrides, so a row written for one would be stored
                      and then ignored - which is what §3.3's `alwaysOn` note already refuses. */}
                  {mayOverride(row) ? (
                    <select
                      aria-label={`${tt('Výnimka pre')} ${row.name}`}
                      data-override-state={row.key}
                      value={row.state}
                      onChange={(change) =>
                        setOverrideEdits((current) => ({
                          ...current,
                          [row.key]: change.target.value as OverrideState,
                        }))
                      }
                      className={cn(field, 'w-auto min-w-[11rem]')}
                    >
                      <option value="inherit">{tt('Podľa balíčka')}</option>
                      <option value="granted">{tt('Pridané navyše')}</option>
                      <option value="revoked">{tt('Odobrané')}</option>
                    </select>
                  ) : (
                    <span
                      className="text-[11px] text-muted-foreground max-w-[18rem]"
                      data-override-locked={row.key}
                      title={overrideLockReason(row)}
                    >
                      <T>Vždy</T>
                    </span>
                  )}

                  {/* What the franchise would end up with, because the answer is a set union of
                      three layers and nobody should have to do it in their head. */}
                  <StatusBadge
                    label={wouldHave(row) ? tt('má') : tt('nemá')}
                    color={wouldHave(row) ? 'emerald' : 'zinc'}
                  />
                </div>
              ))}
            </div>

            <div className="px-5 py-3 border-t border-border flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-overrides-save
                disabled={!overridesChanged(overrides, storedOverrides) || putOverrides.isPending}
                onClick={saveOverrides}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/40 bg-primary/10 text-primary text-xs font-semibold disabled:opacity-40"
              >
                <Save className="w-3.5 h-3.5" /> <T>Uložiť výnimky</T>
              </button>
              <p className="text-[11px] text-muted-foreground">
                {/* The endpoint replaces rather than merges, so the screen sends the whole list -
                    the opposite of the franchise PATCH above it, which sends a diff. */}
                <T>Uloží sa celý zoznam výnimiek, nie len zmenená položka.</T>
              </p>
            </div>
          </>
        )}
      </section>

      {/* §3.7 - the administrators. `PATCH`/`DELETE …/admins/{id}` had no caller either. */}
      <section
        className="bg-card border border-border rounded-2xl overflow-hidden"
        data-franchise-admins
      >
        <div className="px-5 py-3 border-b border-border bg-muted/20">
          <h2 className="font-bold text-foreground text-sm">
            <T>Správcovia</T>
          </h2>
        </div>
        {/* FB-826, §3.155 - **replaced, not annotated.** `adminsOf(undefined)` is `[]`, so a failed read rendered
            *Táto franšíza nemá správcu* about a live tenant: evidential, and the action it invites is seeding an
            administrator (§10A.276's endpoint) for a franchise that already has one. A warning above that sentence
            leaves the claim on screen - §3.149's distinction. */}
        {admins.isError ? (
          <p className="p-5 text-xs text-red-500" data-admins-unreadable>
            <T>
              Správcov sa nepodarilo načítať. Neznamená to, že ich franšíza nemá - server neodpovedal.
            </T>
          </p>
        ) : mine.length === 0 ? (
          <p className="p-5 text-xs text-muted-foreground" data-admins-empty>
            <T>Táto franšíza nemá správcu - preto sa do nej nedá vstúpiť.</T>
          </p>
        ) : (
          <div className="divide-y divide-border">
            {mine.map((one) => (
              <div
                key={one.id}
                data-admin-row={one.id}
                className="px-5 py-2.5 flex flex-wrap items-center gap-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{one.name || one.email}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{one.email}</p>
                </div>
                <StatusBadge
                  label={one.active ? tt('aktívny') : tt('deaktivovaný')}
                  color={one.active ? 'emerald' : 'zinc'}
                />
                <button
                  type="button"
                  data-admin-password={one.id}
                  onClick={() => {
                    setNewPassword('')
                    setAdminAction({ id: one.id, name: one.name || one.email, kind: 'password' })
                  }}
                  className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold"
                >
                  <T>Zmeniť heslo</T>
                </button>
                {/* §3.63, FB-648 - this was `disabled={!one.active}` with no way back, so a row
                  * badged *deaktivovaný* could only be undone in SQL. `updateAdmin` has always
                  * taken `{active: true}`. */}
                {((toggle) => (
                  <button
                    type="button"
                    data-admin-toggle={one.id}
                    data-admin-toggle-action={toggle.action}
                    onClick={() =>
                      setAdminAction({
                        id: one.id,
                        name: one.name || one.email,
                        kind: toggle.action,
                      })
                    }
                    className={cn(
                      'px-3 py-1.5 rounded-lg border text-xs font-semibold',
                      toggle.danger
                        ? 'border-red-500/40 text-red-500'
                        : 'border-emerald-500/40 text-emerald-500',
                    )}
                  >
                    {toggle.label}
                  </button>
                ))(adminToggle(one.active))}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* **The confirmation's shape is kept and not modernised away** - §3.5 says *1:1* licenses
          wiring a destructive action, never making it easier to reach. Typing the slug stays. What
          is added is the call, and a dialog that names which of the two things is about to happen:
          `DELETE` deactivates, `?force=true` removes the registry row, and the tenant's database
          survives either way. A screen that let somebody believe otherwise would be dangerous in
          both directions. */}
      {/* §3.13 - the switch §10A.131 built the flag for and deliberately left unbuilt until
          manager/customer was finished. Its own heading rather than a row in the form above: it
          changes what customers see the moment it lands, and the form's save counts changed fields.
        */}
      <section className="bg-card border border-border rounded-2xl overflow-hidden" data-soft-launch>
        <div className="px-5 py-3 border-b border-border">
          <h2 className="font-bold text-sm">
            <T>Zobrazenie zákazníkom</T>
          </h2>
        </div>
        <div className="p-5 space-y-2">
          <p className="text-[11px] text-muted-foreground" data-soft-launch-state>
            {softLaunchState(isHidden)}
          </p>
          <button
            type="button"
            data-soft-launch-toggle={isHidden ? 'show' : 'hide'}
            disabled={patch.isPending || (!isHidden && !mayHide(visibleInGroup))}
            onClick={() => setSoftLaunching(!isHidden)}
            className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold disabled:opacity-40"
          >
            {isHidden ? <T>Zobraziť zákazníkom</T> : <T>Skryť pred zákazníkmi</T>}
          </button>
          {/* Why it is off, with the count - a disabled switch explains nothing. */}
          {!isHidden && hideRefusal(visibleInGroup) !== '' && (
            <p className="text-[11px] text-amber-500" data-soft-launch-refusal>
              {hideRefusal(visibleInGroup)}
            </p>
          )}
        </div>
      </section>

      {/* §3.37 - the charges on this franchise's monthly invoice. Eight endpoints had no caller,
        * the two reads included, so 45,00 EUR a month of the owner's own billing was set by SQL and
        * shown nowhere. Above the retirement block deliberately: these are operating settings, and
        * the destructive section stays last. */}
      {/* §9.702, owner O84 - which registered domain customer links open: the reset link, the e-mail footer, the
        * referral link and the branch picker all read the primary row, and nothing here showed or set it. */}
      <CustomerDomainSection franchiseId={franchiseId} franchise={franchise} />

      <FranchiseBillingItemsSections franchiseId={franchiseId} />

      {/* §3.184, FB-958 - **who shares this franchise's profit, on the franchise.** `grep -c partner` over
        * this file answered 0, while legacy's `FranchiseDetail.tsx` ends with a *Partneri (podiel na zisku)*
        * section. The capability was never missing - `/partners` has the whole editor behind a franchise
        * picker - so the operator's path was: open the franchise, see nothing, go to Partneri, search for it
        * again.
        *
        * **What ranks it is where every other money fact already is**: the package, the price override, the
        * hardware rental, the module billing, the billing address and the decommission are all on this page,
        * and the profit share was the one that was not. §3.138's shape.
        *
        * **Read-only, deliberately.** `/partners` owns the writes, and §3.156 is the entry where this very
        * rule's over-allocation guard reads the same list its claim does - so a second write path onto
        * `partner_franchises` is how that guard comes to disagree with the figure printed beside it. */}
      <FranchisePartnersSection franchiseId={franchiseId} />

      <section
        className="bg-card border border-red-500/40 rounded-2xl overflow-hidden"
        data-danger
      >
        <div className="px-5 py-3 border-b border-red-500/30 bg-red-500/5">
          <h2 className="font-bold text-red-500 text-sm">
            <T>Vyradenie z prevádzky</T>
          </h2>
        </div>
        <div className="p-5 space-y-3">
          <div className="space-y-1">
            <button
              type="button"
              data-deactivate
              disabled={!mayDeactivate(isActive) || retire.isPending}
              onClick={() => setConfirming('deactivate')}
              className="min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold disabled:opacity-40"
            >
              <T>Deaktivovať</T>
            </button>
            <p className="text-[11px] text-muted-foreground" data-deactivate-note>
              {retirementNote(false)}
            </p>
          </div>

          <div className="space-y-1 pt-2 border-t border-border">
            <p className="text-[11px] text-muted-foreground" data-erase-prompt>
              {erasePrompt(slug)}
            </p>
            <input
              data-erase-input
              aria-label={tt('Potvrdenie slugom')}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              className={field}
            />
            <button
              type="button"
              data-erase
              disabled={!eraseConfirmed(typed, slug) || retire.isPending}
              onClick={() => setConfirming('erase')}
              className="flex items-center gap-1.5 min-h-[36px] px-3 rounded-xl border border-red-500/60 text-xs font-semibold text-red-500 disabled:opacity-40"
            >
              <Trash2 className="w-3.5 h-3.5" /> <T>Zmazať franšízu</T>
            </button>
            <p className="text-[11px] text-muted-foreground" data-erase-note>
              {retirementNote(true)}
            </p>
          </div>
          {retire.isPending && <Spinner />}
        </div>
      </section>

      {/* §3.70, owner decision #30 - *"Tunnel only, never the restore."* Three endpoints had no
        * caller in the Tower; the two below are wired and `POST …/restore-database` is not, because
        * it overwrites a live tenant's database with no undo. The comment is here rather than only
        * in the contract so the next census of unwired writes finds the reason beside the gap. */}
      <section className="rounded-2xl border border-border p-4 space-y-3" data-wireguard>
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Network className="w-4 h-4 text-muted-foreground" />
          <T>WireGuard tunel</T>
        </h2>

        {tunnelProvisioned(tunnel) ? (
          <div className="space-y-3">
            {/* The three safe fields, which are exactly the three the Tower's payload carries -
              * FB-624 drops `wireguard_config` and `master_api_key` from it. The **public** key is
              * shown; the private one lives only in the file. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Peer UUID
                </p>
                <p className="font-mono break-all" data-wireguard-peer>
                  {tunnel.wireguardPeerUuid}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Client IP
                </p>
                <p className="font-mono" data-wireguard-ip>
                  {tunnel.wireguardClientIp}
                </p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Public key
                </p>
                <p className="font-mono break-all" data-wireguard-public-key>
                  {tunnel.wireguardPublicKey}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* **The config is downloaded, never rendered.** It is a wg-quick file and carries a
                * private key, so it goes from the response into a file rather than into the DOM -
                * which is legacy's own choice at `FranchiseDetail.tsx:229`. */}
              <button
                type="button"
                data-wireguard-download
                disabled={downloadingConfig}
                onClick={() => {
                  setDownloadingConfig(true)
                  getSuperAdminFranchisesFranchiseIdWireguardConfig(franchiseId)
                    .then((config) => {
                      downloadTextFile(tunnelConfigFilename(slug), String(config))
                      toast({ title: tt('Konfigurácia stiahnutá') })
                    })
                    .catch((caught: unknown) =>
                      toast({
                        variant: 'destructive',
                        title: tt('Konfiguráciu sa nepodarilo stiahnuť'),
                        description: apiErrorMessage(caught, tt('Server neodpovedal.')),
                      }),
                    )
                    .finally(() => setDownloadingConfig(false))
                }}
                className="flex items-center gap-1.5 min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold disabled:opacity-40"
              >
                <Download className="w-3.5 h-3.5" /> <T>Stiahnuť .conf</T>
              </button>
              <a
                data-wireguard-installer="windows"
                href="/api/v1/super-admin/onprem-installer/windows"
                className="flex items-center gap-1.5 min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold leading-[36px]"
              >
                <Download className="w-3.5 h-3.5" /> setup-onprem.ps1
              </a>
              <a
                data-wireguard-installer="linux"
                href="/api/v1/super-admin/onprem-installer/linux"
                className="flex items-center gap-1.5 min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold leading-[36px]"
              >
                <Download className="w-3.5 h-3.5" /> setup-onprem.sh
              </a>
            </div>

            {/* The removal, behind the same confirmation grammar as the erase above: type the slug.
              * Stronger than legacy's `confirm()` on purpose - the registry row is cleared whether
              * or not the firewall agreed, so this is not a reversible press. */}
            <div className="space-y-1 pt-2 border-t border-border">
              <p className="text-[11px] text-muted-foreground" data-wireguard-remove-prompt>
                {tunnelRemovalPrompt(slug)}
              </p>
              <input
                data-wireguard-remove-input
                aria-label={tt('Potvrdenie slugom')}
                value={tunnelTyped}
                onChange={(event) => setTunnelTyped(event.target.value)}
                className={field}
              />
              <button
                type="button"
                data-wireguard-remove
                disabled={!tunnelRemovalConfirmed(tunnelTyped, slug) || removeTunnel.isPending}
                onClick={() =>
                  removeTunnel.mutate(
                    { franchiseId },
                    {
                      onSuccess: () => {
                        setTunnelTyped('')
                        void queryClient.invalidateQueries({
                          queryKey: getGetSuperAdminFranchisesQueryKey(),
                        })
                        toast({ title: tt('WireGuard peer odstránený') })
                      },
                      onError: (caught: unknown) =>
                        toast({
                          variant: 'destructive',
                          title: tt('Peer sa nepodarilo odstrániť'),
                          description: apiErrorMessage(caught, tt('Server neodpovedal.')),
                        }),
                    },
                  )
                }
                className="flex items-center gap-1.5 min-h-[36px] px-3 rounded-xl border border-red-500/60 text-xs font-semibold text-red-500 disabled:opacity-40"
              >
                <Trash2 className="w-3.5 h-3.5" /> <T>Odstrániť peer</T>
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-[11px] text-muted-foreground" data-wireguard-absent>
              <T>Táto prevádzka nemá WireGuard peer. Vytvorením získa prístup do siete.</T>
            </p>
            {/* **A plain button, and §3.70 says why.** Provisioning is idempotent by its own
              * javadoc - it hands back the existing peer rather than cutting a second - so it does
              * not earn a typed confirmation. Friction with nothing behind it teaches an operator
              * to type past the ones that matter. */}
            <button
              type="button"
              data-wireguard-provision
              disabled={provisionTunnel.isPending}
              onClick={() =>
                provisionTunnel.mutate(
                  { franchiseId },
                  {
                    onSuccess: () => {
                      void queryClient.invalidateQueries({
                        queryKey: getGetSuperAdminFranchisesQueryKey(),
                      })
                      toast({ title: tt('WireGuard peer vytvorený') })
                    },
                    onError: (caught: unknown) =>
                      toast({
                        variant: 'destructive',
                        title: tt('Peer sa nepodarilo vytvoriť'),
                        description: apiErrorMessage(caught, tt('Server neodpovedal.')),
                      }),
                  },
                )
              }
              className="flex items-center gap-1.5 min-h-[36px] px-3 rounded-xl border border-border text-xs font-semibold disabled:opacity-40"
            >
              <Network className="w-3.5 h-3.5" /> <T>Vytvoriť peer</T>
            </button>
          </div>
        )}
        {(provisionTunnel.isPending || removeTunnel.isPending) && <Spinner />}
      </section>

      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={confirming === 'erase' ? tt('Odstrániť registráciu?') : tt('Deaktivovať franšízu?')}
        description={retirementNote(confirming === 'erase')}
        confirmLabel={confirming === 'erase' ? tt('Odstrániť') : tt('Deaktivovať')}
        icon={confirming === 'erase' ? <Trash2 className="w-5 h-5" /> : <LogIn className="w-5 h-5" />}
        loading={retire.isPending}
        onConfirm={() => runRetirement(confirming === 'erase')}
      />

      {/* §3.13 - the note is direction-specific, which is the point: showing a branch starts
          sending it real customers, and hiding one does **not** take it offline. */}
      <ConfirmDialog
        open={softLaunching !== null}
        onClose={() => setSoftLaunching(null)}
        title={softLaunching === true ? tt('Skryť pred zákazníkmi?') : tt('Zobraziť zákazníkom?')}
        description={softLaunchNote(softLaunching === true)}
        confirmLabel={softLaunching === true ? tt('Skryť') : tt('Zobraziť')}
        icon={softLaunching === true ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        loading={patch.isPending}
        onConfirm={() => runSoftLaunch(softLaunching === true)}
      />

      {/* §3.7 - both admin actions take effect on sessions that are already open, which is
          `FranchiseAdminController`'s own comment and the one fact a bare *are you sure* would
          withhold. So the confirmation states it and names the person. */}
      {adminAction !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={adminAction.name}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-admin-dialog={adminAction.kind}
        >
          <div
            ref={adminPanelRef}
            onKeyDown={adminKeyDown}
            className="w-full max-w-md rounded-2xl border border-border bg-card p-5 space-y-3"
          >
            <h3 className="font-bold text-sm">
              {adminAction.kind === 'password'
                ? tt('Zmeniť heslo')
                : adminAction.kind === 'activate'
                  ? tt('Aktivovať správcu')
                  : tt('Deaktivovať správcu')}{' '}
              - {adminAction.name}
            </h3>
            <p className="text-xs text-muted-foreground" data-admin-note>
              {adminActionNote(adminAction.kind)}
            </p>

            {adminAction.kind === 'password' && (
              <>
                <input
                  type="password"
                  aria-label={tt('Nové heslo')}
                  data-admin-password-input
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  className={field}
                />
                {newPassword !== '' && passwordProblem(newPassword) !== '' && (
                  <p className="text-xs text-red-500" data-admin-password-problem>
                    {passwordProblem(newPassword)}
                  </p>
                )}
              </>
            )}

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                data-dialog-cancel
                onClick={() => setAdminAction(null)}
                className="flex-1 min-h-[36px] rounded-xl border border-border text-xs font-semibold"
              >
                <T>Zrušiť</T>
              </button>
              <button
                type="button"
                data-admin-confirm
                disabled={
                  (adminAction.kind === 'password' && passwordProblem(newPassword) !== '') ||
                  patchAdmin.isPending ||
                  removeAdmin.isPending
                }
                onClick={() => {
                  const target = adminAction
                  const done = () => {
                    setAdminAction(null)
                    setNewPassword('')
                    void queryClient.invalidateQueries()
                  }
                  const failed = () =>
                    toast({
                      variant: 'destructive',
                      title: tt('Zmenu sa nepodarilo uložiť'),
                      description: '',
                    })
                  if (target.kind === 'password') {
                    patchAdmin.mutate(
                      { franchiseId, adminId: target.id, data: { password: newPassword } },
                      { onSuccess: done, onError: failed },
                    )
                    return
                  }
                  if (target.kind === 'activate') {
                    // §3.63 - the exact inverse of the `DELETE`, which is
                    // `UPDATE employees SET active = false, token_version = …+1`. The counter is not
                    // decremented by either call, which is why the note does not promise a session back.
                    patchAdmin.mutate(
                      { franchiseId, adminId: target.id, data: adminActivePatch(true) },
                      { onSuccess: done, onError: failed },
                    )
                    return
                  }
                  removeAdmin.mutate(
                    { franchiseId, adminId: target.id },
                    { onSuccess: done, onError: failed },
                  )
                }}
                className={cn(
                  'flex-1 min-h-[36px] rounded-xl border text-xs font-bold disabled:opacity-40',
                  // Giving access back is not destructive, so it does not borrow the red button.
                  adminAction.kind === 'activate'
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-500'
                    : 'border-red-500/40 bg-red-500/10 text-red-500',
                )}
              >
                {adminAction.kind === 'password' ? (
                  <T>Zmeniť</T>
                ) : adminAction.kind === 'activate' ? (
                  <T>Aktivovať</T>
                ) : (
                  <T>Deaktivovať</T>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}

/**
 * A franchise's profit-share partners, listed on the franchise - §3.184, FB-958.
 *
 * **Its own component because its own read.** The detail page already fans out six queries and this is a
 * seventh that nothing else on the page needs; keeping it separate means a failed partner read cannot reach
 * the package editor or the billing address, which is §9.528's per-section rule.
 *
 * **`sharesState` rather than a bare list**, because §3.156 is the entry where an empty `sharesOf(undefined)`
 * told the operator *Žiadny podiel nie je rozdelený* about a franchise whose partner holds 30 %. Here the
 * franchise is always known - it is the page's own id - so only `sharesFailed` can move it off `known`, and
 * that is passed rather than hard-coded so the rule stays the one `/partners` uses.
 */
function FranchisePartnersSection({ franchiseId }: { franchiseId: number }) {
  const links = useGetSuperAdminFranchisesFranchiseIdPartners(franchiseId)
  const shares = useMemo(() => sharesOf(links.data), [links.data])
  /* §3.184 - **named rather than read inline, and the ratchet is why.** `failed-read-withholds-the-claim`
     collects a failure flag off a declaration and reads it **to the end of the line**, so `links.isError`
     buried inside a multi-line object literal was invisible to it and this gate read as `isLoading` alone -
     on a screen that does consult the failure. **It is better code for the same reason**: the component now
     says what it depends on where a reader meets the gate, rather than four lines up inside an argument. */
  const sharesFailed = links.isError
  const view = sharesState({ sharesFailed, franchisesFailed: false, franchiseCount: 1, franchiseId })
  const unknown = sharesAreUnknown(view)

  return (
    <section className="bg-card border border-border rounded-2xl overflow-hidden" data-franchise-partners>
      <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-bold text-foreground text-sm">
          <T>Partneri (podiel na zisku)</T>
        </h2>
        {/* §3.184 - **the link carries this franchise**, because sending an operator to a picker they then
          * have to search is the journey this section exists to remove. `/partners` owns the writes. */}
        <Link
          href={`/partners?franchise=${franchiseId}`}
          data-franchise-partners-edit={franchiseId}
          className="text-xs underline text-primary"
        >
          <T>Spravovať podiely</T>
        </Link>
      </div>

      <div className="p-5 space-y-3">
        {links.isLoading && (
          <div className="py-4 flex justify-center" data-franchise-partners-loading>
            <Spinner />
          </div>
        )}

        {/* The failed read says so rather than claiming nothing is allocated - §3.156's own defect. */}
        {unknown && <ReadFailed marker="franchise-partners">{sharesNote(view)}</ReadFailed>}

        {!links.isLoading && !unknown && (
          <>
            {/* §3.184 - **the total, and what stays with the firm.** A per-partner list without it is
              * arithmetic the operator would do by hand, and `isOverAllocated` colours it so a franchise
              * whose shares exceed 100 % is visible here rather than only inside the editor. */}
            <p
              data-franchise-partners-total
              className={cn(
                'text-xs',
                isOverAllocated(shares) ? 'text-red-500 font-bold' : 'text-muted-foreground',
              )}
            >
              {allocationNote(shares)}
            </p>

            {shares.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-franchise-partners-none>
                <T>Žiadny partner nemá podiel na tejto prevádzke.</T>
              </p>
            ) : (
              <ul className="space-y-1">
                {shares.map((share) => (
                  <li
                    key={share.partnerId}
                    data-franchise-partner={share.partnerId}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <span>{share.partnerName}</span>
                    <span className="tabular-nums font-semibold">
                      {formatDecimal(share.sharePct, 2)} %
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </section>
  )
}
